import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  IDropdownData,
  PreviewQuestionAnswerDetailsSaved,
  RenderPreview,
} from './buyer-preview.models';
import { BuyerPreviewService } from './buyer-preview.service';
import { checkAndFormatDate } from '../shared/date.utils';

@Injectable({
  providedIn: 'root',
})
export class HandleAnswerService {
  constructor() {}

  sanitizer = inject(DomSanitizer);
  private _buyerPreviewService = inject(BuyerPreviewService);
  mode: string = '';

  // SET ANSWERS
  setAnswersFor(
    qnObj: RenderPreview,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    if (qnObj.answered && qnObj.nodeLogic.selectedAnswers.length !== 0) {
      if (qnObj.data[1].type === 'dropdown') {
        qnObj.data[1].dropdowndata.find((dropdownData: IDropdownData) =>
          qnObj.nodeLogic.selectedAnswers.includes(dropdownData.answer)
        ).selected = true;
        this.setDropdownInput(qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'multiselect-dropdown') {
        qnObj.data[1].multiselectDropdowndata
          .filter((dropdownData: IDropdownData) =>
            qnObj.nodeLogic.selectedAnswers.includes(dropdownData.answer)
          )
          .forEach((option) => (option.selected = true));
        this.setDropdownInput(qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'date') {
        this.setInputValue('DATE', qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'textbox') {
        this.setInputValue('NUMBER', qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'radio') {
        qnObj.data[1].radiodata.find((radiodata: IDropdownData) =>
          qnObj.nodeLogic.selectedAnswers?.includes(radiodata.answer)
        ).selected = true;
        this.setRadioInput(qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'checkbox') {
        qnObj.data[1].checkboxdata
          ?.filter((checkboxdata: IDropdownData) =>
            qnObj.nodeLogic.selectedAnswers?.includes(checkboxdata.answer)
          )
          ?.forEach((option) => (option.selected = true));
        this.setCheckboxInput(qnObj, triggerSubjectAction);
      } else if (qnObj.data[1].type === 'textarea') {
        this.setTextAreaInput(qnObj, triggerSubjectAction);
      }
    }
  }

  setDropdownInput(qnObj, triggerSubjectAction) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('select'))?.forEach(
      (selectEleTarget) => {
        if (qnObj.data[1].type === 'dropdown') {
          this.singleSelectDropdownHandler(
            selectEleTarget,
            qnObj,
            triggerSubjectAction
          );
        } else if (qnObj.data[1].type === 'multiselect-dropdown') {
          this.multiSelectDropdownHandler(
            selectEleTarget,
            qnObj,
            triggerSubjectAction
          );
        }
      }
    );

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  setCheckboxInput(qnObj, triggerSubjectAction) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;

      const checkboxWrapperTarget =
        eventTarget?.parentElement?.parentElement?.parentElement;

      checkboxWrapperTarget.setAttribute(
        'id',
        eventTarget.parentElement.id
          ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
          ?.toString()
      );
      const allCheckboxContainersArr = Array.from(
        checkboxWrapperTarget.children
      );
      const isOptionChecked = (option: HTMLInputElement) => {
        return qnObj.data[1].checkboxdata
          ?.filter((checkboxdata: IDropdownData) => checkboxdata.selected)
          ?.map((x) => x.optionid)
          ?.includes(option.id.replace('Checkbox_', ''));
      };

      const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
        (x) => x?.firstChild?.firstChild as HTMLInputElement
      );

      allCheckboxesArr
        ?.filter((option: HTMLInputElement) => isOptionChecked(option))
        ?.forEach((checkedOptionEle: HTMLInputElement) => {
          checkedOptionEle.setAttribute('checked', 'true');
          checkedOptionEle.checked = true;
        });

      if (allCheckboxesArr?.length !== 0) {
        const checkedOptionsArr = allCheckboxesArr.filter((cb) => cb.checked);
        // remove select attribute for all the existing options
        allCheckboxesArr.forEach((option: HTMLInputElement) => {
          option.removeAttribute('checked');
          option.checked = false;

          if (this.mode === 'previewDisabled') {
            option.setAttribute('disabled', 'true');
            option.disabled = true;
          }
        });

        //add select attribute for all the checked options
        checkedOptionsArr.forEach((checkedOptionEle: HTMLInputElement) => {
          checkedOptionEle.setAttribute('checked', 'true');
          checkedOptionEle.checked = true;
        });

        const modifiedFieldEle = document.createElement('div');
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex;justify-content:left'
        );
        modifiedFieldEle.innerHTML = checkboxWrapperTarget.outerHTML;

        const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
          localStorage.getItem(
            checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString()
          )
        );

        const latestFieldAnswerValue = checkedOptionsArr.map(
          (item) => (item.nextSibling as Text).data
        );

        const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
            modifiedFieldEle.outerHTML
          ),
          answerUniqueId: checkedOptionsArr.map((item) => item.id),
          answerValue: latestFieldAnswerValue,
          isAnswerValid: checkedOptionsArr.length > 0,
          isAnswerModified: !(
            oldFieldAnswer?.answerValue?.toString() ===
            latestFieldAnswerValue?.toString()
          ),
        };

        localStorage.setItem(
          checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString(),
          JSON.stringify(fieldAnswer)
        );

        if (triggerSubjectAction === 'setAction') {
          this._buyerPreviewService.helper$.next(
            'set on preview question input element'
          );
        }
      }
    });

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  setRadioInput(qnObj, triggerSubjectAction) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {

      const selectedRadioEle = eventTarget;

      const isChecked =
        eventTarget.value ===
        qnObj.data[1].radiodata.find(
          (radiodata: IDropdownData) => radiodata.selected
        ).optionid;
      const selection = isChecked
        ? (eventTarget.nextSibling as Text).data
        : null;
      if (selection && isChecked) {
        //apply checked attribute to the selected radio        
        selectedRadioEle.setAttribute('checked', 'true');
        selectedRadioEle.checked = true;

        const modifiedFieldEle = document.createElement('div');
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex;justify-content:left'
        );
        modifiedFieldEle.innerHTML = eventTarget.outerHTML;

        const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
          localStorage.getItem(
            eventTarget.name.replace('Radio_', '').toString()
          )
        );

        const latestFieldAnswerValue =
          triggerSubjectAction === 'setAction'
            ? qnObj.nodeLogic.selectedAnswers ?? null
            : selection
            ? [selection]
            : null;

        const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
            modifiedFieldEle.outerHTML
          ),
          answerUniqueId: [eventTarget.value],
          answerValue: latestFieldAnswerValue,
          isAnswerValid: Boolean(eventTarget.value),
          isAnswerModified: !(
            oldFieldAnswer?.answerValue?.toString() ===
            latestFieldAnswerValue?.toString()
          ),
        };
        localStorage.setItem(
          eventTarget.name.replace('Radio_', '').toString(),
          JSON.stringify(fieldAnswer)
        );

        if (triggerSubjectAction === 'setAction') {
          this._buyerPreviewService.helper$.next(
            'set on preview question input element'
          );
        }
      } else {
        selectedRadioEle.removeAttribute('checked');
        selectedRadioEle.checked = false;
      }

      if (this.mode === 'previewDisabled') {
        eventTarget.setAttribute('disabled', 'true');
        eventTarget.disabled = true;
      }
    });

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  singleSelectDropdownHandler(
    eventTarget: HTMLSelectElement,
    qnObj: RenderPreview,
    triggerSubjectAction
  ) {
    const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);
    const selectedOptionEle = optionsArr.find(
      (option: HTMLOptionElement) =>
        option.value ===
        qnObj.data[1].dropdowndata?.find(
          (dropdownData: IDropdownData) => dropdownData.selected
        )?.optionid
    );
    const selection = selectedOptionEle ? selectedOptionEle.text : '';

    //apply selected attribute to the selected option
    optionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;

      if (this.mode === 'previewDisabled') {
        option.setAttribute('disabled', 'true');
        option.disabled = true;
      }
    });

    if (selectedOptionEle && selection.toLowerCase() !== 'select option') {
      selectedOptionEle.setAttribute('selected', 'true');
      selectedOptionEle.selected = true;
    }
    //apply selected attribute to the selected option

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.id.replace('Dropdown_', '').toString())
    );

    const latestFieldAnswerValue =
      selection.toLowerCase() !== 'select option' ? [selection] : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML
      ),
      answerUniqueId: [eventTarget.value],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };

    localStorage.setItem(
      eventTarget.id.replace('Dropdown_', '').toString(),
      JSON.stringify(fieldAnswer)
    );

    if (triggerSubjectAction === 'setAction') {
      this._buyerPreviewService.helper$.next(
        'set on preview question input element'
      );
    }
  }

  multiSelectDropdownHandler(
    multiSelectTarget: HTMLSelectElement,
    qnObj: RenderPreview,
    triggerSubjectAction
  ) {
    const allOptionsArr = Array.from(
      multiSelectTarget.options as HTMLOptionsCollection
    );

    const isOptionSelected = (option: HTMLOptionElement) =>
      qnObj.data[1].multiselectDropdowndata
        ?.filter((dropdownData: IDropdownData) => dropdownData.selected)
        ?.map((x) => x.optionid)
        ?.includes(option.value);

    allOptionsArr?.forEach((option: HTMLOptionElement) => {
      if (isOptionSelected(option)) {
        const selectedOptionEle = option;
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      } else {
        const unselectedOptionEle = option;
        unselectedOptionEle.selected = false;
        unselectedOptionEle.removeAttribute('selected');
      }

      if (this.mode === 'previewDisabled') {
        option.setAttribute('disabled', 'true');
        option.disabled = true;
      }
    });

    if (multiSelectTarget.selectedOptions) {
      const selectedOptionsArr = Array.from(
        multiSelectTarget.selectedOptions as HTMLOptionsCollection
      );

      // remove select attribute for all the existing options
      allOptionsArr.forEach((option: HTMLOptionElement) => {
        option.removeAttribute('selected');
        option.selected = false;
      });

      //add select attribute for all the selected options
      selectedOptionsArr.forEach((selectedOptionEle: HTMLOptionElement) => {
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = multiSelectTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          multiSelectTarget.id.replace('MultiselectDropdown_', '').toString()
        )
      );

      const latestFieldAnswerValue = selectedOptionsArr.map(
        (item) => item.text
      );

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: selectedOptionsArr.map((item) => item.value),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(multiSelectTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        multiSelectTarget.id.replace('MultiselectDropdown_', '').toString(),
        JSON.stringify(fieldAnswer)
      );

      if (triggerSubjectAction === 'setAction') {
        this._buyerPreviewService.helper$.next(
          'set on preview question input element'
        );
      }
    }
  }

  setInputValue(inputType, qnObj, triggerSubjectAction) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input')).forEach((eventTarget) => {

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(eventTarget.id.toString())
      );
      const latestFieldAnswerValue = qnObj.nodeLogic.selectedAnswers ?? null;

      if(inputType === 'DATE') {
        eventTarget.setAttribute('value', checkAndFormatDate(qnObj.nodeLogic.selectedAnswers[0]) ?? '');
        eventTarget.value = checkAndFormatDate(qnObj.nodeLogic.selectedAnswers[0]) ?? '';
      } else {
        eventTarget.setAttribute('value', qnObj.nodeLogic.selectedAnswers[0]);
        eventTarget.value = qnObj.nodeLogic.selectedAnswers[0];
      }

      if (inputType === 'NUMBER') {
        qnObj.valid = eventTarget.validity.valid;
      }

      if (this.mode === 'previewDisabled') {
        eventTarget.setAttribute('disabled', 'true');
        eventTarget.disabled = true;
      }

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: inputType === 'DATE' ? latestFieldAnswerValue?.map(date => checkAndFormatDate(date)) : latestFieldAnswerValue,
        isAnswerValid:
          inputType === 'NUMBER'
            ? eventTarget.validity.valid
            : Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(eventTarget.id, JSON.stringify(fieldAnswer));

      if (triggerSubjectAction === 'setAction') {
        this._buyerPreviewService.helper$.next(
          'set on preview question input element'
        );
      }
    });
    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  setTextAreaInput(qnObj, triggerSubjectAction) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('textarea')).forEach((eventTarget) => {

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textareaID_', '').toString()
        )
      );

      const latestFieldAnswerValue = qnObj.nodeLogic.selectedAnswers ?? null;

      eventTarget.value = qnObj.nodeLogic.selectedAnswers[0];
      eventTarget.setAttribute('value', qnObj.nodeLogic.selectedAnswers[0]);
      eventTarget.innerHTML = qnObj.nodeLogic.selectedAnswers[0];
      eventTarget.setAttribute('innerHTML', qnObj.nodeLogic.selectedAnswers[0]);
      eventTarget.textContent = qnObj.nodeLogic.selectedAnswers[0];
      eventTarget.setAttribute(
        'textContent',
        qnObj.nodeLogic.selectedAnswers[0]
      );

      if (this.mode === 'previewDisabled') {
        eventTarget.setAttribute('disabled', 'true');
        eventTarget.disabled = true;
      }

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('textareaID_', ''),
        JSON.stringify(fieldAnswer)
      );

      if (triggerSubjectAction === 'setAction') {
        this._buyerPreviewService.helper$.next(
          'set on preview question input element'
        );
      }
    });
    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  // CLEAR ANSWERS
  clearAnswersFor(qnObj: RenderPreview) {
    if (qnObj.answered && qnObj.nodeLogic.selectedAnswers.length !== 0) {
      qnObj.answered = null;
      qnObj.nodeLogic.selectedAnswers = null;
      if (qnObj?.data[1]?.type === 'dropdown') {
        this.resetDropdownInput(qnObj);
        qnObj.data[1]?.dropdowndata?.map(
          (optData: IDropdownData) => (optData.selected = false)
        );
      } else if (qnObj?.data[1]?.type === 'multiselect-dropdown') {
        this.resetMultiselectDropdownInput(qnObj);
        qnObj.data[1]?.multiselectDropdowndata?.map(
          (optData: IDropdownData) => (optData.selected = false)
        );
      } else if (qnObj?.data[1]?.type === 'date') {
        this.resetDateInput(qnObj);
      } else if (qnObj?.data[1]?.type === 'textbox') {
        this.resetTextBoxInput(qnObj);
      } else if (qnObj?.data[1]?.type === 'textarea') {
        this.resetTextAreaInput(qnObj);
      } else if (qnObj?.data[1]?.type === 'radio') {
        qnObj.data[1].radiodata?.map(
          (optData: IDropdownData) => (optData.selected = false)
        );
        this.resetRadioInput(qnObj);
      } else if (qnObj?.data[1]?.type === 'checkbox') {
        qnObj.data[1].checkboxdata?.map(
          (optData: IDropdownData) => (optData.selected = false)
        );
        this.resetCheckboxInput(qnObj);
      }
    }
  }

  resetDropdownInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('select'))?.forEach(
      (selectEleTarget) => {
        localStorage.removeItem(selectEleTarget.id.replace('Dropdown_', ''));
        const optionsArr = Array.from(
          selectEleTarget.options as HTMLOptionsCollection
        );
        const selectedOptionEle = optionsArr?.find(
          (option: HTMLOptionElement) => option.value === selectEleTarget.value
        );

        //remove selected attribute from selected option
        optionsArr?.forEach((option: HTMLOptionElement) => {
          option.removeAttribute('selected');
          option.selected = false;
        });
      }
    );

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetMultiselectDropdownInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('select'))?.forEach(
      (multiSelectEleTarget) => {
        localStorage.removeItem(
          multiSelectEleTarget.id.replace('MultiselectDropdown_', '')
        );
        const allOptionsArr = Array.from(
          multiSelectEleTarget.options as HTMLOptionsCollection
        );

        // remove select attribute for all the existing options
        allOptionsArr?.forEach((option: HTMLOptionElement) => {
          option.removeAttribute('selected');
          option.selected = false;
        });
      }
    );

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetDateInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input')).forEach((dateInputEle) => {
      localStorage.removeItem(dateInputEle.id);
      dateInputEle.removeAttribute('value');
      dateInputEle.value = '';
    });
    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetTextBoxInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input')).forEach((inputEle) => {
      localStorage.removeItem(inputEle.id.replace('textboxID_', ''));
      inputEle.removeAttribute('value');
      inputEle.value = '';
    });
    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetTextAreaInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('textarea')).forEach(
      (textareaInputEle) => {
        localStorage.removeItem(textareaInputEle.id.replace('textareaID_', ''));
        textareaInputEle.value = '';
        textareaInputEle.removeAttribute('value');
        textareaInputEle.innerHTML = '';
        textareaInputEle.removeAttribute('innerHTML');
        textareaInputEle.textContent = '';
        textareaInputEle.removeAttribute('textContent');
      }
    );
    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetRadioInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {

      localStorage.removeItem(eventTarget.id.replace('Radio_', ''));

      const selectedRadioEle = eventTarget;

      selectedRadioEle.checked = false;
      selectedRadioEle.removeAttribute('checked');
    });

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }

  resetCheckboxInput(qnObj: RenderPreview) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      qnObj?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;
      const checkboxWrapperTarget =
        eventTarget?.parentElement?.parentElement?.parentElement;

      checkboxWrapperTarget.setAttribute(
        'id',
        eventTarget.parentElement.id
          ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
          ?.toString()
      );

      localStorage.removeItem(
        checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '')
      );

      const allCheckboxContainersArr = Array.from(
        checkboxWrapperTarget.children
      );

      const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
        (x) => x?.firstChild?.firstChild as HTMLInputElement
      );

      allCheckboxesArr?.forEach((optionEle: HTMLInputElement) => {
        optionEle.removeAttribute('checked');
        optionEle.checked = false;
      });
    });

    const modifiedHtmlStr = html.body.innerHTML;
    qnObj.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
  }
}
